<div class="admin-stats-dashboard flex h-full min-h-0 w-full flex-col overflow-hidden">
  <div class="shrink-0 px-5 pt-5">
    <h4 class="m-0 text-3xl font-bold tracking-tight text-zinc-900 dark:text-white">Cancellations</h4>
    @if (rangeLabel()) {
    <p class="m-0 mt-2 max-w-4xl text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
      What brands and agencies told us on the way out, {{ rangeLabel() }}. Counts
      cover every off-boarding attempt — including the ones that ended in a save
      or were abandoned part-way.
    </p>
    } @else {
    <p class="m-0 mt-2 max-w-4xl text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
      What brands and agencies told us on the way out.
    </p>
    }
  </div>

  <!-- One filter row, above everything it scopes. -->
  <admin-stats-period-filter (periodChange)="onPeriodChange($event)">
    <div class="flex items-center gap-2" role="group" aria-label="Account type">
      <button
        type="button"
        class="rounded-lg border px-3 py-1.5 text-xs font-medium"
        [class]="getScopeButtonClass('all')"
        [attr.aria-pressed]="scope() === 'all'"
        (click)="setScope('all')"
      >
        All
      </button>
      <button
        type="button"
        class="rounded-lg border px-3 py-1.5 text-xs font-medium"
        [class]="getScopeButtonClass('business')"
        [attr.aria-pressed]="scope() === 'business'"
        (click)="setScope('business')"
      >
        Brands
      </button>
      <button
        type="button"
        class="rounded-lg border px-3 py-1.5 text-xs font-medium"
        [class]="getScopeButtonClass('agency')"
        [attr.aria-pressed]="scope() === 'agency'"
        (click)="setScope('agency')"
      >
        Agencies
      </button>
    </div>
  </admin-stats-period-filter>

  @if (loadingState() === 'error' && error()) {
  <div
    class="mx-5 mt-4 shrink-0 rounded-xl border border-rose-200 bg-rose-50 p-3 dark:border-rose-500/30 dark:bg-rose-500/10"
    role="alert"
  >
    <p class="m-0 text-sm text-rose-700 dark:text-rose-300">{{ error() }}</p>
  </div>
  }

  <div class="flex min-h-0 flex-1 flex-col gap-5 overflow-auto px-5 pb-5 scrollbar-transparent">
    @if (loadingState() === 'forbidden') {
    <section class="admin-stats-panel mt-5 rounded-2xl p-8 text-center">
      <h5 class="m-0 text-lg font-semibold text-zinc-900 dark:text-white">GodMode access required</h5>
      <p class="m-0 mx-auto mt-2 max-w-md text-sm text-zinc-600 dark:text-zinc-400">
        Exit surveys name the accounts that left, quote what they wrote about us
        and carry their billing ids, so they are limited to GodMode admins. Ask
        someone with that role if you need a cancellation looked up.
      </p>
    </section>
    } @else if (loadingState() === 'loading' && !insights()) {
    <atom-loading-inline title="Loading cancellations" message="Reading the exit surveys…" />
    } @else if (insights(); as data) {
    <div [class.is-refreshing]="isRefreshing()" class="flex flex-col gap-5">
      <!-- KPI row -->
      <div class="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        @for (card of kpiCards(); track card.key) {
        <section class="admin-stats-panel rounded-2xl p-5">
          <p class="m-0 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
            {{ card.label }}
          </p>
          <p class="m-0 mt-4 text-4xl font-bold leading-none text-zinc-900 dark:text-white xl:text-5xl">
            {{ card.display }}
          </p>
          @if (card.showTrend) {
          @let trend = getKpiTrend(card.current, card.previous);
          <p class="m-0 mt-4 text-sm">
            <span class="font-semibold" [class]="trend.trendClass">{{ trend.arrow }} {{ trend.percentLabel }}</span>
            <span class="text-zinc-500 dark:text-zinc-500"> {{ trend.comparisonLabel }}</span>
          </p>
          }
          <p class="m-0 mt-2 text-xs text-zinc-500 dark:text-zinc-500">{{ card.hint }}</p>
        </section>
        }
      </div>

      @if (primaryMoney(); as money) {
      @if (money.unpricedRowCount > 0 || otherMoney().length > 0) {
      <p class="m-0 -mt-2 text-xs text-zinc-500 dark:text-zinc-500">
        @if (money.unpricedRowCount > 0) {
        <span>
          {{ money.unpricedRowCount }} of {{ money.unpricedRowCount + money.pricedRowCount }}
          off-boardings predate subscription-value capture, so they are missing from
          the revenue figures.
        </span>
        }
        @for (other of otherMoney(); track other.currencyCode) {
        <span class="ml-2">
          Also {{ other.currencyCode | uppercase }}: {{ other.pricedRowCount }} priced rows, reported separately.
        </span>
        }
      </p>
      }
      }

      <!-- Why they leave + would they come back -->
      <div class="grid gap-4 xl:grid-cols-5">
        <section class="admin-stats-panel rounded-2xl p-5 xl:col-span-3">
          <h5 class="m-0 text-sm font-semibold text-zinc-900 dark:text-white">Why they leave</h5>
          <p class="m-0 mt-1 text-xs text-zinc-500 dark:text-zinc-400">
            Bar length is how often the reason was given; the split is what the
            account went on to do. Select a bar to see its follow-up answers.
          </p>
          @if (reasonChart(); as chart) {
          @if (chart.hasData) {
          <div
            echarts
            class="mt-3 block h-72 w-full"
            [options]="chart.option"
            (chartInit)="onReasonChartInit($event)"
          ></div>
          } @else {
          <div class="mt-3 flex h-72 items-center justify-center rounded-xl border border-dashed border-zinc-200 dark:border-white/10">
            <p class="m-0 text-sm text-zinc-500">Nobody gave a reason in this period.</p>
          </div>
          }
          }

          <!-- Table view twin: every number in the chart, readable without colour. -->
          <table class="mt-4 min-w-full text-xs">
            <caption class="sr-only">Cancellation reasons by outcome</caption>
            <thead>
              <tr class="text-zinc-500 dark:text-zinc-400">
                <th scope="col" class="py-1 text-left font-medium">Reason</th>
                <th scope="col" class="py-1 text-right font-medium">Total</th>
                @for (series of outcomeSeries; track series.key) {
                <th scope="col" class="py-1 text-right font-medium">{{ series.label }}</th>
                }
              </tr>
            </thead>
            <tbody class="divide-y divide-zinc-100 dark:divide-white/5">
              @for (reason of data.reasons; track reason.id) {
              @if (reason.count > 0) {
              <tr class="text-zinc-700 dark:text-zinc-300">
                <th scope="row" class="py-1 text-left font-normal">{{ reason.label }}</th>
                <td class="py-1 text-right tabular-nums font-semibold">{{ reason.count }}</td>
                <td class="py-1 text-right tabular-nums">{{ reason.cancelled }}</td>
                <td class="py-1 text-right tabular-nums">{{ reason.kept }}</td>
                <td class="py-1 text-right tabular-nums">{{ reason.paused }}</td>
                <td class="py-1 text-right tabular-nums">{{ reason.abandoned }}</td>
              </tr>
              }
              }
            </tbody>
          </table>
        </section>

        <section class="admin-stats-panel rounded-2xl p-5 xl:col-span-2">
          <h5 class="m-0 text-sm font-semibold text-zinc-900 dark:text-white">Would they come back?</h5>
          <p class="m-0 mt-1 text-xs text-zinc-500 dark:text-zinc-400">
            Asked of everyone, answered by {{ returnTotal() }}. Select a band to
            list those accounts.
          </p>

          <p class="m-0 mt-5 text-4xl font-bold leading-none text-zinc-900 dark:text-white">
            {{ data.rates.winbackPool }}
          </p>
          <p class="m-0 mt-1 text-xs text-zinc-500 dark:text-zinc-500">
            in the winback pool — answered “Very likely” or “Maybe”
          </p>

          @if (returnTotal() > 0) {
          <div class="mt-5 flex gap-0.5" aria-hidden="true">
            @for (option of data.returnLikelihood; track option.id) {
            @if (option.count > 0) {
            <span
              class="h-2.5 rounded-full"
              [style.width]="returnSegmentWidth(option.count)"
              [style.background-color]="returnSegmentColor(option.id)"
            ></span>
            }
            }
          </div>

          <ul class="mt-4 flex flex-col gap-2">
            @for (option of data.returnLikelihood; track option.id) {
            <li>
              <button
                type="button"
                class="flex w-full items-center gap-2 rounded-lg px-1 py-1 text-left hover:bg-zinc-50 dark:hover:bg-white/5"
                (click)="openResponsesFor(option.id)"
              >
                <span
                  class="size-2.5 shrink-0 rounded-full"
                  [style.background-color]="returnSegmentColor(option.id)"
                  aria-hidden="true"
                ></span>
                <span class="flex-1 text-xs text-zinc-700 dark:text-zinc-300">{{ option.label }}</span>
                <span class="text-xs tabular-nums font-semibold text-zinc-900 dark:text-white">{{ option.count }}</span>
                <span class="w-10 text-right text-xs tabular-nums text-zinc-500">{{ formatPercent(option.percent) }}</span>
              </button>
            </li>
            }
          </ul>
          } @else {
          <div class="mt-5 rounded-xl border border-dashed border-zinc-200 p-4 dark:border-white/10">
            <p class="m-0 text-sm text-zinc-500">Nobody answered this question in the period.</p>
          </div>
          }
        </section>
      </div>

      <!-- Follow-ups + timeline -->
      <div class="grid gap-4 xl:grid-cols-2">
        <section class="admin-stats-panel rounded-2xl p-5">
          <h5 class="m-0 text-sm font-semibold text-zinc-900 dark:text-white">
            @if (selectedFollowUp(); as group) {
            {{ group.question }}
            } @else {
            Follow-up answers
            }
          </h5>
          <p class="m-0 mt-1 text-xs text-zinc-500 dark:text-zinc-400">
            The wording shown is the one those accounts actually saw.
          </p>

          @if (data.followUps.length > 0) {
          <div class="mt-3 flex flex-wrap gap-2">
            @for (group of data.followUps; track group.reasonId) {
            <button
              type="button"
              class="rounded-lg border px-2.5 py-1 text-xs font-medium"
              [class]="getReasonChipClass(group.reasonId)"
              [attr.aria-pressed]="isReasonSelected(group.reasonId)"
              (click)="selectReason(group.reasonId)"
            >
              {{ group.reasonLabel }}
            </button>
            }
          </div>
          }

          @if (followUpChart(); as chart) {
          @if (chart.hasData) {
          <div echarts class="mt-3 block h-64 w-full" [options]="chart.option"></div>
          } @else {
          <div class="mt-3 flex h-64 items-center justify-center rounded-xl border border-dashed border-zinc-200 dark:border-white/10">
            <p class="m-0 text-sm text-zinc-500">No follow-up answers in this period.</p>
          </div>
          }
          }
        </section>

        <section class="admin-stats-panel rounded-2xl p-5">
          <h5 class="m-0 text-sm font-semibold text-zinc-900 dark:text-white">Over time</h5>
          <p class="m-0 mt-1 text-xs text-zinc-500 dark:text-zinc-400">
            Off-boardings per {{ data.range.bucket }}.
            @if (data.scopeSplit.agency > 0) {
            {{ data.scopeSplit.business }} from brands, {{ data.scopeSplit.agency }} from agencies.
            }
          </p>
          @if (timelineChart(); as chart) {
          @if (chart.hasData) {
          <div echarts class="mt-3 block h-72 w-full" [options]="chart.option"></div>
          } @else {
          <div class="mt-3 flex h-72 items-center justify-center rounded-xl border border-dashed border-zinc-200 dark:border-white/10">
            <p class="m-0 text-sm text-zinc-500">Nothing happened in this period.</p>
          </div>
          }
          }
        </section>
      </div>

      <!-- Verbatims -->
      <section class="admin-stats-panel rounded-2xl p-5">
        <div class="flex items-start justify-between gap-4">
          <div>
            <h5 class="m-0 text-sm font-semibold text-zinc-900 dark:text-white">In their words</h5>
            <p class="m-0 mt-1 text-xs text-zinc-500 dark:text-zinc-400">
              The most recent free-text answers to “Anything else you want us to hear?”
            </p>
          </div>
          <button
            type="button"
            class="shrink-0 rounded-lg px-2.5 py-1 text-xs font-medium text-violet-700 hover:bg-violet-50 dark:text-violet-300 dark:hover:bg-violet-500/10"
            (click)="openAllResponses()"
          >
            View all responses →
          </button>
        </div>

        @if (data.verbatims.length > 0) {
        <ul class="mt-4 flex flex-col divide-y divide-zinc-100 dark:divide-white/5">
          @for (verbatim of data.verbatims; track verbatim.id) {
          <li>
            <button
              type="button"
              class="flex w-full gap-3 rounded-lg px-1 py-3 text-left hover:bg-zinc-50 dark:hover:bg-white/5"
              (click)="openResponse(verbatim.id)"
            >
              <span class="size-9 shrink-0 overflow-hidden rounded-full bg-zinc-100 dark:bg-white/5">
                <atom-ldg-img
                  inputClass="object-cover"
                  w="full"
                  h="full"
                  radius="full"
                  [src]="imageService.getImageSource(verbatim.account.logoUrl, 'gallery')"
                />
              </span>
              <span class="min-w-0 flex-1">
                <span class="flex flex-wrap items-center gap-2">
                  <span class="text-sm font-semibold text-zinc-900 dark:text-white">
                    {{ verbatim.account.name ?? 'Unknown account' }}
                  </span>
                  @if (verbatim.reasonLabel) {
                  <span class="text-xs text-zinc-500 dark:text-zinc-400">{{ verbatim.reasonLabel }}</span>
                  }
                  <span
                    class="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset"
                    [class]="outcomeChipClass(verbatim.outcome)"
                  >
                    {{ outcomeLabel(verbatim.outcome) }}
                  </span>
                  <span class="text-xs text-zinc-400">{{ verbatim.createdDate | date: 'dd MMM yy' }}</span>
                </span>
                <span class="mt-1 block text-sm leading-relaxed text-zinc-700 dark:text-zinc-300">
                  “{{ verbatim.comment }}”
                </span>
              </span>
            </button>
          </li>
          }
        </ul>
        } @else {
        <div class="mt-4 rounded-xl border border-dashed border-zinc-200 p-6 text-center dark:border-white/10">
          <p class="m-0 text-sm text-zinc-500">Nobody left a comment in this period.</p>
        </div>
        }
      </section>
    </div>
    }
  </div>
</div>

@if (openResponseId(); as responseId) {
<admin-cancellation-detail-drawer [responseId]="responseId" (closed)="closeResponse()" />
}
